/**
 * Génère une couleur d'avatar basée sur le nom de l'utilisateur
 * @param {string} name - Le nom de l'utilisateur
 * @returns {Object} - Un objet contenant les classes CSS pour le gradient
 */
export function getAvatarColor(name) {
    // Palettes de couleurs harmonieuses pour les avatars avec codes hex
    const colorPalettes = [
        { from: "#3b82f6", to: "#2563eb", text: "#ffffff" }, // blue
        { from: "#10b981", to: "#059669", text: "#ffffff" }, // emerald
        { from: "#a855f7", to: "#9333ea", text: "#ffffff" }, // purple
        { from: "#ec4899", to: "#db2777", text: "#ffffff" }, // pink
        { from: "#6366f1", to: "#4f46e5", text: "#ffffff" }, // indigo
        { from: "#f97316", to: "#ea580c", text: "#ffffff" }, // orange
        { from: "#14b8a6", to: "#0d9488", text: "#ffffff" }, // teal
        { from: "#ef4444", to: "#dc2626", text: "#ffffff" }, // red
        { from: "#f59e0b", to: "#d97706", text: "#ffffff" }, // amber
        { from: "#06b6d4", to: "#0891b2", text: "#ffffff" }, // cyan
        { from: "#84cc16", to: "#65a30d", text: "#ffffff" }, // lime
        { from: "#8b5cf6", to: "#7c3aed", text: "#ffffff" }, // violet
        { from: "#f43f5e", to: "#e11d48", text: "#ffffff" }, // rose
        { from: "#0ea5e9", to: "#0284c7", text: "#ffffff" }, // sky
        { from: "#64748b", to: "#475569", text: "#ffffff" }, // slate
    ];

    // Calculer un hash simple basé sur le nom
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        const char = name.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convertir en entier 32bit
    }

    // Utiliser le hash pour sélectionner une couleur
    const colorIndex = Math.abs(hash) % colorPalettes.length;
    return colorPalettes[colorIndex];
}

/**
 * Génère les initiales à partir d'un nom complet
 * @param {string} name - Le nom complet
 * @returns {string} - Les initiales (max 2 caractères)
 */
export function getInitials(name) {
    if (!name) return "?";

    const names = name.trim().split(" ");
    if (names.length >= 2) {
        return (
            names[0].charAt(0) + names[names.length - 1].charAt(0)
        ).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
}

/**
 * Génère les classes CSS complètes pour un avatar avec initiales
 * @param {string} name - Le nom de l'utilisateur
 * @param {string} size - Taille de l'avatar ('sm', 'md', 'lg', 'xl')
 * @returns {Object} - Un objet contenant les classes CSS et les initiales
 */
export function generateAvatarProps(name, size = "md") {
    const colors = getAvatarColor(name);
    const initials = getInitials(name);

    // Définir les tailles
    const sizes = {
        xs: "w-6 h-6 text-xs",
        sm: "w-8 h-8 text-sm",
        md: "w-12 h-12 text-lg",
        lg: "w-16 h-16 text-xl",
        xl: "w-20 h-20 text-2xl",
        xxl: "w-24 h-24 text-3xl",
    };

    const sizeClasses = sizes[size] || sizes.md;

    return {
        className: `${sizeClasses} rounded-full flex items-center justify-center font-semibold shadow-sm`,
        style: {
            background: `linear-gradient(135deg, ${colors.from} 0%, ${colors.to} 100%)`,
            color: colors.text,
        },
        initials,
        colors,
    };
}
